import type { ComponentProps, ReactNode } from 'react'

import { Button } from '@adverity/ds-component/button'
import type * as DsButton from '@adverity/ds-component/button'
import { keyframes } from '@emotion/core'
import type { Keyframes } from '@emotion/core'
import { isNullish, isString } from 'remeda'

import { DrawerInternal } from './drawer-internal'
import type { Props as DrawerInternalProps } from './drawer-internal'
import { DialogActions } from '../dialog/dialog-actions'

type ButtonBaseProps = DsButton.ButtonBaseProps

type FooterButtonExtraProps = Pick<ComponentProps<typeof Button>, 'variant' | 'onClick' | 'type'>

type FooterAction = Omit<ButtonBaseProps, 'children' | 'asChild'> & {
    label?: ReactNode
    prefix?: ReactNode
    suffix?: ReactNode
    tooltip?: ReactNode
}

type Props = Omit<DrawerInternalProps, 'footer'> & {
    contain?: boolean
    footer?: {
        additionalActions?: ReactNode
        cancelAction?: FooterAction | null
        applyAction?: FooterAction | null
    }
}

const FooterButton = ({
    action,
    defaultLabel,
    ...buttonProps
}: { action: FooterAction; defaultLabel: string } & FooterButtonExtraProps) => {
    const { label, prefix, suffix, tooltip, ...rest } = action

    if (isString(label)) {
        return (
            <Button {...buttonProps} {...rest} prefix={prefix} suffix={suffix} tooltip={tooltip}>
                {label}
            </Button>
        )
    }

    if (!isNullish(label)) {
        return (
            <Button {...buttonProps} {...rest} prefix={prefix} suffix={suffix} tooltip={tooltip} label={defaultLabel}>
                {label}
            </Button>
        )
    }

    return (
        <Button {...buttonProps} {...rest} prefix={prefix} suffix={suffix} tooltip={tooltip}>
            {defaultLabel}
        </Button>
    )
}

const slideRight = keyframes({
    from: {
        transform: 'translateX(100%)',
    },
    to: {
        transform: '0',
    },
})

const slideBottom = keyframes({
    from: {
        transform: 'translateY(100%)',
    },
    to: {
        transform: '0',
    },
})

const slideLeft = keyframes({
    from: {
        transform: 'translateX(-100%)',
    },
    to: {
        transform: '0',
    },
})

const PLACEMENT_VIEW_TRANSITION_MAPPING: Record<Props['placement'], { name: string; keyframes: Keyframes }> = {
    left: {
        name: 'drawer-left',
        keyframes: slideLeft,
    },
    right: {
        name: 'drawer-right',
        keyframes: slideRight,
    },
    bottom: {
        name: 'drawer-bottom',
        keyframes: slideBottom,
    },
} as const

export const createViewTransitionCss = (placement: keyof typeof PLACEMENT_VIEW_TRANSITION_MAPPING) => {
    const viewTransitionName = PLACEMENT_VIEW_TRANSITION_MAPPING[placement].name
    const animationName = PLACEMENT_VIEW_TRANSITION_MAPPING[placement].keyframes

    return {
        /* Enter */
        [`::view-transition-new(${viewTransitionName}):only-child, ::view-transition-old(${viewTransitionName}):only-child`]:
            {
                animationName,
                animationDuration: '0.2s',
            },

        /* Leave */
        [`::view-transition-old(${viewTransitionName}):only-child`]: {
            animationDirection: 'reverse',
        },
    }
}

export const Drawer = ({ children, footer, onClose, ...props }: Props) => (
    <DrawerInternal
        onClose={onClose}
        {...props}
        css={[{ viewTransitionName: PLACEMENT_VIEW_TRANSITION_MAPPING[props.placement].name }, props.css]}
        footer={
            footer ? (
                <DialogActions additionalActions={footer.additionalActions}>
                    {footer.cancelAction && (
                        <FooterButton
                            action={footer.cancelAction}
                            defaultLabel="Cancel"
                            variant="secondary"
                            onClick={onClose}
                        />
                    )}
                    {footer.applyAction && (
                        <FooterButton action={footer.applyAction} defaultLabel="Apply" type="submit" />
                    )}
                </DialogActions>
            ) : undefined
        }
    >
        {children}
    </DrawerInternal>
)

Drawer.displayName = 'Drawer'
